// Shared browser-to-GCS upload primitives.
//
// Both upload surfaces PUT bytes straight to a presigned GCS URL, never through
// Jupiter: the document library (Venus) and assistant-chat attachments (Venus +
// Neptune). These helpers live here so there is one implementation of the PUT and
// one of the size formatting, rather than a copy per surface.

/** Human-readable file size, German locale ("1,4 MB"). "—" for null/0/invalid. */
export function formatFileSize(bytes: number | null | undefined): string {
    if (bytes == null || bytes === 0) return "—";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    // Guard against NaN/invalid calculations
    if (!Number.isFinite(i) || i < 0 || i >= units.length) return "—";
    const size = bytes / Math.pow(k, i);
    return `${size.toLocaleString("de-DE", { maximumFractionDigits: 1 })} ${units[i]}`;
}

/**
 * Single-PUT upload to a v4 presigned GCS URL (5-minute expiry).
 *
 * `onProgress` receives cumulative uploaded bytes and the average speed in bytes/s,
 * which is what the document library's progress and ETA display needs; callers that
 * only want a percentage divide by `file.size`. Aborting the signal aborts the
 * request and rejects.
 *
 * Files large enough to need a resumable session (>2 GB, or long-running uploads that
 * must survive a reload) use the resumable path in Venus's FileUploadProvider instead.
 */
export function uploadFileToGcs(
    url: string,
    file: File,
    onProgress: (uploadedBytes: number, speed: number) => void,
    signal: AbortSignal,
): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const startTime = Date.now();

        xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
                const timeTaken = (Date.now() - startTime) / 1000;
                const speed = timeTaken > 0 ? event.loaded / timeTaken : 0;
                onProgress(event.loaded, speed);
            }
        });

        xhr.upload.addEventListener("error", () => reject(new Error("Upload failed")));
        xhr.addEventListener("error", () => reject(new Error("Request failed")));
        xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(new Error(`Upload failed with status ${xhr.status}`));
            }
        });

        xhr.open("PUT", url);
        xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
        xhr.send(file);

        signal.addEventListener("abort", () => {
            xhr.abort();
            reject(new Error("Upload aborted"));
        });
    });
}
